import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useLocalData } from "../hooks/useLocalData";
import { onAgentIndividuals } from "../services/individualService";
import { getEnabledServices, getBillingConfig } from "../services/billingConfigService";
import {
  ArrowLeft, ArrowRight, Plus, X, Calendar, Clock, MapPin,
  Mic, CheckCircle, ChevronRight, Repeat, AlertTriangle, Users,
} from "lucide-react";
import BottomNav from "../components/BottomNav";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function toDateStr(d) {
  return d.toLocaleDateString();
}

function toInputDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseInputDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d, 12);
}

export default function SchedulePage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const {
    appointments,
    addAppointment, updateAppointment, deleteAppointment,
    completeAppointment,
  } = useLocalData();

  const [individuals, setIndividuals] = useState([]);
  const [view, setView] = useState("week");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddVisit, setShowAddVisit] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [newVisit, setNewVisit] = useState({
    individualId: "",
    date: toInputDate(new Date()),
    time: "",
    locationType: "Out-of-Clinic",
    location: "",
    serviceType: "",
    recurring: "none",
    recurringWeeks: 4,
  });

  // Load individuals from Firestore
  useEffect(() => {
    if (!profile) return;
    const uid = profile.uid || profile.id;
    if (!uid) return;
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    const unsub = onAgentIndividuals(uid, org, (data) => {
      setIndividuals(data.sort((a, b) => (a.lastName || "").localeCompare(b.lastName || "")));
    });
    return () => unsub();
  }, [profile]);

  // Load billing config for service picker
  useEffect(() => {
    const org = profile?.organization || localStorage.getItem("fieldnotes_user_org") || "default-org";
    getBillingConfig(org).then(config => {
      const opts = getEnabledServices(config);
      setServiceOptions(opts);
      if (opts.length > 0 && !newVisit.serviceType) {
        setNewVisit(prev => ({ ...prev, serviceType: opts[0].value }));
      }
    });
  }, [profile]);

  // Week dates
  const weekDates = useMemo(() => {
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [selectedDate]);

  const getApptsForDate = (dateStr) => {
    return appointments
      .filter(a => a.date === dateStr)
      .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  };

  const todayStr = new Date().toLocaleDateString();
  const selectedStr = selectedDate.toLocaleDateString();
  const selectedAppts = getApptsForDate(selectedStr);

  const upcoming = useMemo(() => {
    const now = new Date();
    const week = new Date(now);
    week.setDate(week.getDate() + 7);
    return appointments
      .filter(a => {
        if (a.status === "completed") return false;
        const parts = (a.date || "").split("/");
        if (parts.length !== 3) return false;
        const ad = new Date(parts[2], parts[0] - 1, parts[1]);
        return ad >= now && ad <= week;
      })
      .sort((a, b) => {
        const da = new Date(a.date);
        const db = new Date(b.date);
        if (da.getTime() !== db.getTime()) return da - db;
        return (a.time || "").localeCompare(b.time || "");
      });
  }, [appointments]);

  const countForDate = (d) => getApptsForDate(toDateStr(d)).length;

  const prevWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    setSelectedDate(d);
  };
  const nextWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    setSelectedDate(d);
  };
  const goToday = () => setSelectedDate(new Date());

  const handleAddVisit = () => {
    if (!newVisit.individualId || !newVisit.time || !newVisit.date) return;
    const ind = individuals.find(i => i.id === newVisit.individualId);
    const name = ind ? `${ind.firstName} ${ind.lastName}` : "Unknown";
    const address = newVisit.location || ind?.address || "";

    const baseDate = parseInputDate(newVisit.date);

    const dates = [baseDate];
    if (newVisit.recurring !== "none") {
      const interval = newVisit.recurring === "weekly" ? 7 : 14;
      const weeks = Math.min(newVisit.recurringWeeks || 4, 12);
      for (let w = 1; w < weeks; w++) {
        const d = new Date(baseDate);
        d.setDate(d.getDate() + (interval * w));
        dates.push(d);
      }
    }

    dates.forEach(d => {
      addAppointment({
        individualId: newVisit.individualId,
        individualName: name,
        date: d.toLocaleDateString(),
        time: newVisit.time,
        locationType: newVisit.locationType,
        location: newVisit.location,
        address,
        serviceType: newVisit.serviceType,
        recurring: newVisit.recurring !== "none",
      });
    });

    setShowAddVisit(false);
    setNewVisit({
      individualId: "",
      date: toInputDate(new Date()),
      time: "",
      locationType: "Out-of-Clinic",
      location: "",
      serviceType: serviceOptions[0]?.value || "",
      recurring: "none",
      recurringWeeks: 4,
    });
  };

  const handleStartSession = (appt) => {
    const ind = individuals.find(i => i.id === appt.individualId);
    navigate("/session/new/" + appt.individualId, {
      state: {
        appointmentId: appt.id,
        individualName: appt.individualName,
        individualId: appt.individualId,
        dob: ind?.dob || "",
        locationType: appt.locationType,
        location: appt.address || appt.location,
        serviceType: appt.serviceType,
        knownMedications: ind?.medications || [],
        knownDiagnoses: ind?.diagnoses || [],
      },
    });
  };

  const isToday = (d) => toDateStr(d) === todayStr;
  const isSelected = (d) => toDateStr(d) === selectedStr;

  return (
    <div className="min-h-screen bg-surface-primary">
      {/* Header */}
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <div className="flex items-center gap-3">
          <Calendar size={20} className="text-blue-400" />
          <span className="text-lg font-bold text-gray-100">Schedule</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={goToday}
            className="px-3 py-1.5 rounded-lg text-[10px] font-bold"
            style={{
              background: "rgba(79,209,197,0.08)",
              color: "#4fd1c5",
              border: "1px solid rgba(79,209,197,0.15)",
            }}
          >
            Today
          </button>
          <button
            onClick={() => setShowAddVisit(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{
              background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
              color: "white",
            }}
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </header>

      <div className="px-5 py-4 pb-28">

        {/* Week Navigator */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={prevWeek} className="p-2">
            <ArrowLeft size={16} className="text-gray-500" />
          </button>
          <div className="text-sm font-bold text-gray-200">
            {MONTHS[weekDates[0].getMonth()]} {weekDates[0].getDate()} – {weekDates[6].getDate()}, {weekDates[6].getFullYear()}
          </div>
          <button onClick={nextWeek} className="p-2">
            <ArrowRight size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Week Calendar Strip */}
        <div className="grid grid-cols-7 gap-1 mb-5">
          {weekDates.map((d, i) => {
            const count = countForDate(d);
            const today = isToday(d);
            const selected = isSelected(d);
            return (
              <button
                key={i}
                onClick={() => setSelectedDate(d)}
                className="flex flex-col items-center py-2 rounded-xl transition-colors"
                style={{
                  background: selected
                    ? "rgba(49,130,206,0.2)"
                    : today
                      ? "rgba(79,209,197,0.06)"
                      : "transparent",
                  border: selected
                    ? "1px solid rgba(49,130,206,0.3)"
                    : today
                      ? "1px solid rgba(79,209,197,0.1)"
                      : "1px solid transparent",
                }}
              >
                <div className="text-[9px] font-bold text-gray-500 uppercase">{DAYS[i]}</div>
                <div
                  className="text-base font-bold mt-0.5"
                  style={{ color: selected ? "#63b3ed" : today ? "#4fd1c5" : "#a0aec0" }}
                >
                  {d.getDate()}
                </div>
                <div className="flex gap-0.5 mt-1" style={{ minHeight: 6 }}>
                  {Array.from({ length: Math.min(count, 4) }, (_, j) => (
                    <div
                      key={j}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: selected ? "#63b3ed" : "#4a5568" }}
                    />
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected Day Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-bold tracking-[1.5px] uppercase" style={{ color: "#63b3ed" }}>
            {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <div className="text-[10px] text-gray-600 font-semibold">
            {selectedAppts.length} visit{selectedAppts.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Appointments for Selected Day */}
        {selectedAppts.length === 0 ? (
          <div
            className="glass-card p-6 text-center mb-4"
            style={{ borderStyle: "dashed" }}
          >
            <Calendar size={28} className="mx-auto mb-2" style={{ color: "#2d3748" }} />
            <div className="text-sm text-gray-500">No visits scheduled</div>
            <button
              onClick={() => {
                setNewVisit(prev => ({ ...prev, date: toInputDate(selectedDate) }));
                setShowAddVisit(true);
              }}
              className="mt-3 px-4 py-2 rounded-xl text-xs font-semibold"
              style={{ background: "rgba(99,179,237,0.1)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
            >
              Schedule a Visit
            </button>
          </div>
        ) : (
          selectedAppts.map(appt => (
            <div
              key={appt.id}
              className="glass-card p-4 mb-3"
              style={{
                opacity: appt.status === "completed" ? 0.5 : 1,
                borderLeft: appt.status === "completed" ? "3px solid #48bb78" : "3px solid #63b3ed",
              }}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Clock size={11} className="text-gray-500" />
                    <span className="text-xs text-gray-400 font-semibold">{appt.time}</span>
                    {appt.recurring && <Repeat size={10} className="text-purple-400" />}
                  </div>
                  <div className="text-base font-bold text-gray-100 mt-1">{appt.individualName}</div>
                  <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                    <MapPin size={10} /> {appt.locationType} — {appt.address || appt.location || "TBD"}
                  </div>
                  <div className="mt-1.5">
                    <span
                      className="text-[9px] font-bold px-2 py-0.5 rounded"
                      style={{ background: "rgba(79,209,197,0.08)", color: "#4fd1c5" }}
                    >
                      {appt.serviceType}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {appt.status === "completed" ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-green-500/10 text-green-400">
                      <CheckCircle size={10} /> Done
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => handleStartSession(appt)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold"
                        style={{
                          background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
                          color: "white",
                        }}
                      >
                        <Mic size={11} /> Start
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(appt.id)}
                        className="text-[10px] text-gray-600 font-semibold"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              {deleteConfirm === appt.id && (
                <div className="mt-3 p-3 rounded-xl" style={{ background: "rgba(252,129,129,0.05)", border: "1px solid rgba(252,129,129,0.12)" }}>
                  <div className="text-xs text-gray-300 mb-2">Cancel this visit?</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { deleteAppointment(appt.id); setDeleteConfirm(null); }}
                      className="flex-1 py-2 rounded-lg text-xs font-bold"
                      style={{ background: "rgba(252,129,129,0.15)", color: "#fc8181" }}
                    >
                      Yes, Cancel
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold text-gray-500"
                    >
                      Keep
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}

        {/* Upcoming Section */}
        {upcoming.length > 0 && selectedStr === todayStr && (
          <>
            <div className="text-[11px] font-bold tracking-[1.5px] uppercase mt-6 mb-3" style={{ color: "#4fd1c5" }}>
              Upcoming This Week ({upcoming.length})
            </div>
            {upcoming.slice(0, 6).map(appt => (
              <div
                key={appt.id}
                className="glass-card p-3 mb-2 flex items-center justify-between"
                onClick={() => {
                  const parts = (appt.date || "").split("/");
                  if (parts.length === 3) {
                    setSelectedDate(new Date(parts[2], parts[0] - 1, parts[1]));
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <div>
                  <div className="text-sm font-bold text-gray-200">{appt.individualName}</div>
                  <div className="text-[10px] text-gray-500">
                    {appt.date} at {appt.time} · {appt.serviceType}
                  </div>
                </div>
                <ChevronRight size={14} className="text-gray-600" />
              </div>
            ))}
          </>
        )}
      </div>

      {/* ===== ADD VISIT MODAL ===== */}
      {showAddVisit && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/70 px-4 pb-4">
          <div
            className="w-full max-w-sm rounded-2xl overflow-hidden animate-slide-up"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(99,179,237,0.15)",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div className="flex items-center justify-between p-5 pb-3">
              <h3 className="text-lg font-bold text-gray-100">Schedule Visit</h3>
              <button onClick={() => setShowAddVisit(false)}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-3">
              {/* Individual */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">
                  Individual {individuals.length === 0 && <span className="text-red-400">(loading...)</span>}
                </label>
                <select
                  className="field-input text-sm"
                  style={{ appearance: "auto" }}
                  value={newVisit.individualId}
                  onChange={(e) => {
                    const ind = individuals.find(i => i.id === e.target.value);
                    setNewVisit({
                      ...newVisit,
                      individualId: e.target.value,
                      location: ind?.address || "",
                    });
                  }}
                >
                  <option value="">Select individual...</option>
                  {individuals.map(ind => (
                    <option key={ind.id} value={ind.id}>
                      {ind.lastName}, {ind.firstName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Date</label>
                <input
                  type="date"
                  className="field-input text-sm"
                  value={newVisit.date}
                  onChange={(e) => setNewVisit({ ...newVisit, date: e.target.value })}
                />
              </div>

              {/* Time */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Time</label>
                <input
                  type="time"
                  className="field-input text-sm"
                  value={newVisit.time}
                  onChange={(e) => setNewVisit({ ...newVisit, time: e.target.value })}
                />
              </div>

              {/* Service Type */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Service Type</label>
                <select
                  className="field-input text-sm"
                  style={{ appearance: "auto" }}
                  value={newVisit.serviceType}
                  onChange={(e) => setNewVisit({ ...newVisit, serviceType: e.target.value })}
                >
                  {serviceOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} ({opt.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Location Type */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Location Type</label>
                <div className="flex gap-2">
                  {["In-Clinic", "Out-of-Clinic"].map(type => (
                    <button
                      key={type}
                      className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-center"
                      style={{
                        background: newVisit.locationType === type ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
                        color: newVisit.locationType === type ? "#63b3ed" : "#718096",
                        border: `1px solid ${newVisit.locationType === type ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
                      }}
                      onClick={() => setNewVisit({ ...newVisit, locationType: type })}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Address */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Address</label>
                <input
                  className="field-input text-sm"
                  placeholder="e.g. Ind Residence, community center..."
                  value={newVisit.location}
                  onChange={(e) => setNewVisit({ ...newVisit, location: e.target.value })}
                />
              </div>

              {/* Recurring */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">
                  <Repeat size={10} className="inline mr-1" /> Recurring
                </label>
                <div className="flex gap-2">
                  {[
                    { value: "none", label: "One-time" },
                    { value: "weekly", label: "Weekly" },
                    { value: "biweekly", label: "Bi-weekly" },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold text-center"
                      style={{
                        background: newVisit.recurring === opt.value ? "rgba(183,148,244,0.15)" : "rgba(15,23,42,0.5)",
                        color: newVisit.recurring === opt.value ? "#b794f4" : "#718096",
                        border: `1px solid ${newVisit.recurring === opt.value ? "rgba(183,148,244,0.25)" : "rgba(99,179,237,0.06)"}`,
                      }}
                      onClick={() => setNewVisit({ ...newVisit, recurring: opt.value })}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {newVisit.recurring !== "none" && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] text-gray-500">Repeat for</span>
                    <select
                      className="field-input text-xs py-1"
                      style={{ appearance: "auto", width: 60 }}
                      value={newVisit.recurringWeeks}
                      onChange={(e) => setNewVisit({ ...newVisit, recurringWeeks: parseInt(e.target.value) })}
                    >
                      {[2, 4, 6, 8, 12].map(w => (
                        <option key={w} value={w}>{w}</option>
                      ))}
                    </select>
                    <span className="text-[10px] text-gray-500">weeks</span>
                    <span className="text-[10px] text-purple-400 ml-auto">
                      = {newVisit.recurring === "weekly" ? newVisit.recurringWeeks : Math.ceil(newVisit.recurringWeeks / 2)} visits
                    </span>
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm mt-2"
                style={{
                  background: newVisit.individualId && newVisit.time && newVisit.date
                    ? "linear-gradient(135deg, #3182ce, #2b6cb0)"
                    : "rgba(49,130,206,0.3)",
                  opacity: newVisit.individualId && newVisit.time && newVisit.date ? 1 : 0.5,
                }}
                onClick={handleAddVisit}
                disabled={!newVisit.individualId || !newVisit.time || !newVisit.date}
              >
                {newVisit.recurring !== "none"
                  ? `Schedule ${newVisit.recurring === "weekly" ? newVisit.recurringWeeks : Math.ceil(newVisit.recurringWeeks / 2)} Visits`
                  : "Schedule Visit"
                }
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="schedule" />
    </div>
  );
}